import { IOrderClause } from '@tableau/catalog-ui-temp';
import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';
import _ from 'underscore';

import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { collectionItemPositionSortOrder } from '~src/Collections/models/CollectionItemsSortOptions';
import { CollectionItemsResourceQuery, ItemReordering } from '~src/Collections/ResourceQuery/CollectionItemsResourceQuery';
import { ConfirmDragDropOrderDialog } from '~src/Collections/space/ConfirmDragDropOrderDialog';
import { IDataSourceInfo } from '~src/datasource/models/IDataSourceInfo';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { CallReorderItemAction } from '~src/spacesPlatform/actions/CallFavoritesReorder';
import { reorderItem } from '~src/spacesPlatform/actions/ReorderItem';
import { resetContentBusyFetching } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { selectSortOrder } from '~src/spacesPlatform/actions/SelectSortOrderAction';
import { setIgnoreNextOrderUrlChange, setOrderAction } from '~src/spacesPlatform/actions/SetOrderAction';
import { AssetListItem } from '~src/spacesPlatform/models/AssetListItem';
import { OrderKeys } from '~src/spacesPlatform/models/OrderKeys';
import { IExplorableItemTypeInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { selectAssetListItems } from '~src/spacesPlatform/selectors/AssetListSelectors';
import { selectContentFromAppState } from '~src/spacesPlatform/selectors/ContentSelectors';
import { selectContentSort } from '~src/spacesPlatform/selectors/ContentSortSelectors';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { requestFailed } from '../../bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '../../codegen/Localize';


export function reorderCollectionItems(orderings: ItemReordering[], collectionLuid: string) {
  const resource = new CollectionItemsResourceQuery(collectionLuid);
  return resource.reorderCollectionItems(orderings);
}

export interface ReorderCollectionItemInfo {
  itemLuid: string;
  itemType: string;
}

function getCorrespondingCollectionItemInfo(
  content: IExplorableItemTypeInfo,
  assetListItems: readonly AssetListItem[],
): ReorderCollectionItemInfo {

  const datasourceMatchesAssetListItem = (items: readonly AssetListItem[]): ReorderCollectionItemInfo => {
    for(let item of items){
      if(item.luid === (content as IDataSourceInfo).luid && item.objectType.toLowerCase() === content.contentType.toLowerCase()){
        // data source stores content luid in the `datasourceLuid` field instead of `luid`.
        return {
        itemLuid: (content as IDataSourceInfo).datasourceLuid,
        itemType: content.contentType
        };
      }
    }
  };

  const contentMatchesAssetListItem = (items: readonly AssetListItem[]): ReorderCollectionItemInfo => {
    for(let item of items){
      if(item.luid === content.luid && item.objectType.toLowerCase() === content.contentType.toLowerCase()){
        return {
          itemLuid: content.luid,
          itemType: content.contentType
        };
      }
    }
  };

  const item: ReorderCollectionItemInfo = content.contentType === 'datasource'?
    datasourceMatchesAssetListItem(assetListItems):
    contentMatchesAssetListItem(assetListItems);

  return item;
}

export function* cancelReorderItem(action: CallReorderItemAction, spaceInfo: SpaceInfo): SagaIterator{
  // reordering item n to item n+1 then canceling results in fromIndex === indexToMoveAfter
  if (action.fromIndex <= action.indexToMoveAfter) {
    yield put(
      reorderItem(
        action.indexToMoveAfter + 1,
        action.fromIndex - 1,
        spaceInfo
      )
    );
  } else {
    yield put(
      reorderItem(
        action.indexToMoveAfter + 1,
        action.fromIndex,
        spaceInfo
      )
    );
  }
}

export function generateNewCollectionsOrder(
  contentItems: readonly IExplorableItemTypeInfo[],
  assetListItems: readonly AssetListItem[],
): ItemReordering[] {
  const itemOrdering: ItemReordering[] = [];
  let itemIdToMoveAfter = '-1';
  let itemTypeToMoveAfter = 'View';
  // Find the ones that were reordered and return the changes in the order.
  // changes are represented by  ItemReordering interface(itemId, itemIdToMoveAfter).
  for (let i = 0; i < contentItems.length; i++) {
    const item: ReorderCollectionItemInfo = getCorrespondingCollectionItemInfo(contentItems[i], assetListItems);
    itemOrdering.push({ itemId: item.itemLuid, itemType: item.itemType, itemIdToMoveAfter, itemTypeToMoveAfter: itemTypeToMoveAfter });
    itemIdToMoveAfter = item.itemLuid;
    itemTypeToMoveAfter = item.itemType;
  }
  return itemOrdering;
}

function getCollectionIdToMoveAfter(
  action: CallReorderItemAction,
  contentItems: readonly IExplorableItemTypeInfo[],
  assetListItems: readonly AssetListItem[],
): ReorderCollectionItemInfo {
  // If moving to the beginning, the orderCollections API requires that the favoriteIdToMoveAfter should just be -1
  if (action.indexToMoveAfter < 0) {
    return {itemLuid: '-1', itemType: contentItems[0].contentType};
  }
  return getCorrespondingCollectionItemInfo(contentItems[action.indexToMoveAfter], assetListItems);
}

// TODO:TFS1302379 Refactor this function & file, its large and shares a lot of code with favorites reorder
export function* handleCollectionsReorder(action: CallReorderItemAction): SagaIterator {
  try {
    const assetListItems: readonly AssetListItem[] = yield select(
      selectAssetListItems
    );
    const contentItems: readonly IExplorableItemTypeInfo[] = yield select(
      selectContentFromAppState
    );
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);

    const collectionLuid: string = spaceInfo.item?.luid;
    const order: readonly IOrderClause[] = yield select(selectContentSort);

    // call the confirmation dialog when the selected sort option is not "Custom" sort.
    if (order.length !== 0 && order[0].field !== OrderKeys.position) {
      const dialogOptions: ConfirmDragDropOrderDialog.ContentOptions = {
        title: Messages.orderCollectionsConfirmDialogTitle(),
        message: Messages.orderCollectionsConfirmDialogMessage(),
      };
      const result: ConfirmDragDropOrderDialog.ConfirmOrCancel = yield call(
        ConfirmDragDropOrderDialog.open,
        dialogOptions
      );
      // If the user input is "cancel" revert the changes.
      if (result === 'cancel') {
        yield call(cancelReorderItem, action, spaceInfo);
        return;
      }

      // Reorder the items for the user and call the collections API to save the new order.
      const collectionsOrder = generateNewCollectionsOrder(
        contentItems,
        assetListItems
      );
      yield call(reorderCollectionItems, collectionsOrder, collectionLuid);
      _.defer(()=>{
        // we can't just reload the page
        // with the default sort because they'll show things in the incorrect (old) order
        // Instead we have to update the url and have FavoriteSpace *not* fetch new items.
        // And we have to update the store directly because the url change won't trigger it.
        getAppStore().dispatch(setIgnoreNextOrderUrlChange());
        getAppStore().dispatch(setOrderAction(collectionItemPositionSortOrder));
        getAppStore().dispatch(resetContentBusyFetching());
        getAppStore().dispatch(selectSortOrder(collectionItemPositionSortOrder));
      });
      return;
    }

    const sourceItem = contentItems[action.indexToMoveAfter + 1];
    const item: ReorderCollectionItemInfo = getCorrespondingCollectionItemInfo(
      sourceItem,
      assetListItems
    );
    const itemToMove = getCollectionIdToMoveAfter(
      action,
      contentItems,
      assetListItems
    );

    /*
    itemId & itemIdToMoveAfter store the luids for items.
    the reason being API layer is exposed to customers and
    would be confusing to use luids in the request model.
    */
    const orderings: ItemReordering[] = [{
      itemId: item.itemLuid,
      itemType: item.itemType,
      itemIdToMoveAfter: itemToMove.itemLuid,
      itemTypeToMoveAfter: itemToMove.itemType
    }];

    const result: IResult = yield call(
      reorderCollectionItems,
      orderings,
      collectionLuid
    );

    if (!_.isEmpty(result) && result.errors?.length > 0) {
      yield put(
        requestFailed(result.errors[0] as unknown as Error, Messages.collectionsReorderError())
      );
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.collectionsError()));
  }
}
